import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Settings, CheckCircle, XCircle, Loader2, Shield } from 'lucide-react';

const GatewayPermissions = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [tenants, setTenants] = useState([]);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/gateway-permissions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTenants(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Gagal memuat data tenant'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (tenantId, gatewayType, mode) => {
    setChanges(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        [gatewayType]: mode
      }
    }));
  };

  const hasChanges = (tenantId) => {
    return !!changes[tenantId];
  };

  const saveChanges = async (tenant) => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const token = localStorage.getItem('token');
      const updatedPermissions = {
        payment_gateway_mode: changes[tenant.tenant_id]?.payment_gateway_mode || tenant.gateway_permissions.payment_gateway_mode,
        whatsapp_gateway_mode: changes[tenant.tenant_id]?.whatsapp_gateway_mode || tenant.gateway_permissions.whatsapp_gateway_mode,
        smtp_mode: changes[tenant.tenant_id]?.smtp_mode || tenant.gateway_permissions.smtp_mode
      };

      // Use V2 MySQL endpoint
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/gateway-permissions/${tenant.tenant_id}`,
        { gateway_permissions: updatedPermissions },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setTenants(prev => prev.map(t => 
        t.tenant_id === tenant.tenant_id 
          ? { ...t, gateway_permissions: updatedPermissions }
          : t
      ));

      // Clear changes for this tenant
      setChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[tenant.tenant_id];
        return newChanges;
      });

      setMessage({
        type: 'success',
        text: `Gateway permissions untuk ${tenant.tenant_name} berhasil diupdate!`
      });

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving changes:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Gagal menyimpan perubahan'
      });
    } finally {
      setSaving(false);
    }
  };

  const getModeDisplay = (mode) => {
    const modes = {
      global: { label: 'Global', color: 'bg-blue-500', icon: '🌐' },
      self_service: { label: 'Self-Service', color: 'bg-green-500', icon: '🔧' },
      hybrid: { label: 'Hybrid', color: 'bg-purple-500', icon: '🔄' }
    };
    return modes[mode] || modes.global;
  };

  const getCurrentMode = (tenant, gatewayType) => {
    return changes[tenant.tenant_id]?.[gatewayType] || tenant.gateway_permissions[gatewayType];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
        <span className="ml-2 text-lg">Memuat data tenant...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          <h1 className="text-3xl font-bold">Gateway Permissions Management</h1>
        </div>
        <p className="text-gray-600 dark:text-slate-300">
          Kelola mode gateway untuk setiap tenant (Payment, WhatsApp, SMTP)
        </p>
      </div>

      {message.text && (
        <Alert className={`mb-4 ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' :
          'bg-red-50 text-red-800 border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Mode Gateway
          </CardTitle>
          <CardDescription>
            Tentukan bagaimana tenant menggunakan gateway configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🌐</span>
                <h3 className="font-semibold">Global</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                Tenant wajib menggunakan konfigurasi global dari Super Admin
              </p>
            </div>
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🔧</span>
                <h3 className="font-semibold">Self-Service</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                Tenant harus setup dan mengelola gateway mereka sendiri
              </p>
            </div>
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🔄</span>
                <h3 className="font-semibold">Hybrid</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                Tenant bisa memilih menggunakan global atau setup sendiri
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Daftar Tenant</CardTitle>
          <CardDescription>
            {tenants.length} tenant terdaftar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Tenant</TableHead>
                  <TableHead>Payment Gateway</TableHead>
                  <TableHead>WhatsApp Gateway</TableHead>
                  <TableHead>SMTP/Email</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.tenant_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{tenant.tenant_name}</div>
                        <div className="text-xs text-gray-500 dark:text-slate-400">{tenant.tenant_id}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={getCurrentMode(tenant, 'payment_gateway_mode')}
                        onValueChange={(value) => handlePermissionChange(tenant.tenant_id, 'payment_gateway_mode', value)}
                      >
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">🌐 Global</SelectItem>
                          <SelectItem value="self_service">🔧 Self-Service</SelectItem>
                          <SelectItem value="hybrid">🔄 Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={getCurrentMode(tenant, 'whatsapp_gateway_mode')}
                        onValueChange={(value) => handlePermissionChange(tenant.tenant_id, 'whatsapp_gateway_mode', value)}
                      >
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">🌐 Global</SelectItem>
                          <SelectItem value="self_service">🔧 Self-Service</SelectItem>
                          <SelectItem value="hybrid">🔄 Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={getCurrentMode(tenant, 'smtp_mode')}
                        onValueChange={(value) => handlePermissionChange(tenant.tenant_id, 'smtp_mode', value)}
                      >
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">🌐 Global</SelectItem>
                          <SelectItem value="self_service">🔧 Self-Service</SelectItem>
                          <SelectItem value="hybrid">🔄 Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      {hasChanges(tenant.tenant_id) && (
                        <Button
                          size="sm"
                          onClick={() => saveChanges(tenant)}
                          disabled={saving}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Simpan
                            </>
                          ) : (
                            'Simpan'
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
        <p className="text-sm text-blue-800">
          <strong>💡 Tips:</strong>
          <br />
          • <strong>Global</strong>: Cocok untuk tenant baru yang belum siap setup gateway sendiri
          <br />
          • <strong>Self-Service</strong>: Untuk tenant yang ingin full control atas gateway mereka
          <br />
          • <strong>Hybrid</strong>: Memberikan fleksibilitas - tenant bisa menggunakan global atau setup sendiri
        </p>
      </div>
    </div>
  );
};

export default GatewayPermissions;
